import { useTranslation } from 'react-i18next';
import { Panel, PanelSection } from '@/shared/ui';
import { SegmentedField } from '@/shared/forms';
import { PlaceholderType } from '@/api/generated/schemas';
import { AccommodationCategorySelectorField } from '@/modules/products/venua-planner/components/AccommodationCategorySelector';
import { EquipmentSelectorField } from '@/modules/products/venua-planner/components/EquipmentSelector';
import { FacilitySubTypeSelectorField } from '@/modules/products/venua-planner/components/FacilitySubTypeSelector';
import { transportKindOptions, unitPatternOptions } from '../../internal/placeholderEnums';
import { FORM_LABEL_WIDTH } from '../../internal/formLayout';
import type { PlaceholderFormApi } from '../usePlaceholderForm';

/** Panel (3): parametry specifické pro typ — Ubytování / Doprava / ostatní (info). */
export function PlaceholderTypeSpecificPanel({ form }: { form: PlaceholderFormApi }) {
  const { t } = useTranslation('venua-planner');

  return (
    <Panel variant="filled" title={t('schemes.placeholderForm.sections.typeSpecific')}>
      <PanelSection>
        <form.Subscribe selector={(state) => state.values.type}>
          {(type) => {
            if (type === PlaceholderType.Accommodation) {
              return (
                <>
                  <form.Field name="facilitySubType">
                    {(field) => (
                      <FacilitySubTypeSelectorField
                        field={field}
                        mode="single"
                        layout="inline"
                        labelWidth={FORM_LABEL_WIDTH}
                        label={t('schemes.placeholderForm.facilitySubType')}
                        placeholder={t('schemes.placeholderForm.facilitySubTypePlaceholder')}
                        testId="placeholder-facility-subtype"
                      />
                    )}
                  </form.Field>

                  <form.Field name="accommodationCategory">
                    {(field) => (
                      <AccommodationCategorySelectorField
                        field={field}
                        mode="single"
                        layout="inline"
                        labelWidth={FORM_LABEL_WIDTH}
                        label={t('schemes.placeholderForm.accommodationCategory')}
                        placeholder={t('schemes.placeholderForm.accommodationCategoryPlaceholder')}
                        testId="placeholder-accommodation-category"
                      />
                    )}
                  </form.Field>

                  <form.Field name="unitPatterns">
                    {(field) => (
                      <SegmentedField
                        field={field}
                        multiple
                        label={t('schemes.placeholderForm.unitPatterns')}
                        helperText={t('schemes.placeholderForm.unitPatternsHelp')}
                        labelWidth={FORM_LABEL_WIDTH}
                        options={unitPatternOptions(t)}
                      />
                    )}
                  </form.Field>

                  <form.Field name="equipment">
                    {(field) => (
                      <EquipmentSelectorField
                        field={field}
                        mode="multi"
                        layout="inline"
                        labelWidth={FORM_LABEL_WIDTH}
                        label={t('schemes.placeholderForm.equipment')}
                        placeholder={t('schemes.placeholderForm.equipmentPlaceholder')}
                        testId="placeholder-equipment"
                      />
                    )}
                  </form.Field>
                </>
              );
            }

            if (type === PlaceholderType.Transport) {
              return (
                <form.Field name="transportKind">
                  {(field) => (
                    <SegmentedField
                      field={field}
                      label={t('schemes.placeholderForm.transportKind')}
                      labelWidth={FORM_LABEL_WIDTH}
                      required
                      options={transportKindOptions(t)}
                      testId="placeholder-transport-kind"
                    />
                  )}
                </form.Field>
              );
            }

            return (
              <p className="text-phx-text-muted text-phx-sm">
                {t('schemes.placeholderForm.noExtraParams')}
              </p>
            );
          }}
        </form.Subscribe>
      </PanelSection>
    </Panel>
  );
}
